import { LessonPrepStatusResponse } from '@/types/teacher-request'
import { sortLessonsByDateTime } from '@/utils/lessonSort'

interface PrepLessonListProps {
  requestId: string
  lessonStatus: LessonPrepStatusResponse[]
  selectedLessonId?: string
  onLessonClick: (lessonId: string) => void
}

export function PrepLessonList({ lessonStatus, selectedLessonId, onLessonClick }: PrepLessonListProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted': return 'bg-green-500'
      case 'has_prep': return 'bg-blue-500'
      case 'prep_requested': return 'bg-amber-500'
      case 'no_prep': return 'bg-red-500'
      default: return 'bg-gray-400'
    }
  }

  const getStatusBadge = (status: string, prepTitle?: string) => {
    switch (status) {
      case 'accepted':
        return <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">{prepTitle || 'Prep'}</span>
      case 'has_prep':
        return <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">{prepTitle || 'Prep'}</span>
      case 'prep_requested':
        return <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">angefragt</span>
      case 'no_prep':
        return <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-medium">kein Prep</span>
      default:
        return <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-medium">offen</span>
    }
  }

  const sortedLessons = sortLessonsByDateTime(lessonStatus)
  const groupedByPackage = sortedLessons.reduce((acc, lesson) => {
    const key = lesson.package_id || 'no-package'
    if (!acc[key]) acc[key] = []
    acc[key].push(lesson)
    return acc
  }, {} as Record<string, LessonPrepStatusResponse[]>)

  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-y-auto h-full">
      <div className="p-3 border-b bg-gray-50 rounded-t-xl">
        <h3 className="text-sm font-semibold text-gray-700">Lektionen & Pakete</h3>
        <p className="text-xs text-gray-400 mt-0.5">Klicken zum Auswählen</p>
      </div>
      <div className="p-2 space-y-1">
        {Object.entries(groupedByPackage).map(([packageId, lessons]) => (
          <div key={packageId}>
            <div className="px-2 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">
              {packageId === 'no-package' ? 'Ohne Paket' : `Paket`}
            </div>
            {lessons.map(lesson => (
              <div
                key={lesson.lesson_request_id}
                role="button"
                tabIndex={0}
                onClick={() => onLessonClick(lesson.lesson_request_id)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onLessonClick(lesson.lesson_request_id) }}
                className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition ${
                  selectedLessonId === lesson.lesson_request_id
                    ? 'bg-blue-50 border border-blue-200'
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className={`w-2.5 h-2.5 rounded-full ${getStatusColor(lesson.status)} flex-shrink-0`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-800">{lesson.time_slot_name}</div>
                  <div className="text-xs text-gray-500">{lesson.date}{lesson.subject_name && ` · ${lesson.subject_name}`}{lesson.klass_name && ` · ${lesson.klass_name}`}</div>
                </div>
                {getStatusBadge(lesson.status, lesson.prep_title)}
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="p-3 border-t bg-gray-50 rounded-b-xl">
        <div className="text-[10px] font-semibold text-gray-400 uppercase mb-1.5">Legende</div>
        <div className="space-y-1">
          <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-green-500" /><span className="text-[11px] text-gray-600">Von allen Vertretern akzeptiert</span></div>
          <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-blue-500" /><span className="text-[11px] text-gray-600">Mit Prep verknüpft</span></div>
          <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-amber-500" /><span className="text-[11px] text-gray-600">Prep angefragt</span></div>
          <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-red-500" /><span className="text-[11px] text-gray-600">Kein Prep</span></div>
          <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-gray-400" /><span className="text-[11px] text-gray-600">Offen</span></div>
        </div>
      </div>
    </div>
  )
}
